import { useState } from 'react';
import { X, Loader2 } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { toast } from 'sonner';
import { apiFetch } from '../lib/apiClient';
import ImagePickerField from './ImagePickerField';
import TruckLocationPicker from './TruckLocationPicker';
import { hasExactLocation } from '../lib/truckLocation';

const DEFAULT_HOURS = {
  monday: { open: '10:00', close: '18:00' },
  tuesday: { open: '10:00', close: '18:00' },
  wednesday: { open: '10:00', close: '18:00' },
  thursday: { open: '10:00', close: '18:00' },
  friday: { open: '10:00', close: '16:00' }
};

const CAMPUS_LOCATIONS = [
  'Sidney Smith Hall',
  'Robarts Library',
  'Bahen Centre',
  'Medical Sciences Building',
  'Hart House',
  'University College',
  'Gerstein Library',
  'Knox College',
  'Innis College',
  'New College'
];

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export default function TruckForm({ onClose, onCreated, defaultPayoutEmail = '' }) {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    photo_url: '',
    location: '',
    cuisine_type: '',
    payout_email: defaultPayoutEmail,
    operating_hours: DEFAULT_HOURS
  });
  const [exactLocation, setExactLocation] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.location) {
      toast.error('Name and location are required');
      return;
    }
    if (!EMAIL_PATTERN.test(formData.payout_email.trim())) {
      toast.error('A valid Interac e-Transfer email is required for payouts');
      return;
    }

    setLoading(true);

    try {
      const response = await apiFetch('/api/trucks', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...formData,
          payout_email: formData.payout_email.trim(),
          exact_location: hasExactLocation(exactLocation)
            ? {
              lat: Number(exactLocation.lat),
              lng: Number(exactLocation.lng),
              note: exactLocation.note || null,
            }
            : null,
        }),
      });

      if (response.ok) {
        const truck = await response.json();
        onCreated(truck);
      } else {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to create truck');
      }
    } catch (error) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div 
      className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="bg-card rounded-2xl w-full max-w-md max-h-[90vh] overflow-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between p-4 border-b">
          <h2 className="font-bold text-lg">Create Food Truck</h2>
          <button onClick={onClose} className="p-2 hover:bg-muted rounded-lg">
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-4 space-y-4">
          <div>
            <Label htmlFor="name">Truck Name *</Label>
            <Input 
              id="name"
              value={formData.name}
              onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
              placeholder="e.g., Taco Express"
              required
              data-testid="truck-name-input"
            />
          </div>

          <div>
            <Label htmlFor="description">Description</Label>
            <Input 
              id="description"
              value={formData.description}
              onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
              placeholder="What makes your food special?"
              data-testid="truck-description-input"
            />
          </div>

          <div>
            <Label htmlFor="cuisine_type">Cuisine Type</Label>
            <Input 
              id="cuisine_type"
              value={formData.cuisine_type}
              onChange={(e) => setFormData(prev => ({ ...prev, cuisine_type: e.target.value }))}
              placeholder="e.g., Mexican, Vietnamese, American"
              data-testid="truck-cuisine-input"
            />
          </div>

          <div>
            <Label htmlFor="location">Campus Location *</Label>
            <select
              id="location"
              value={formData.location}
              onChange={(e) => setFormData(prev => ({ ...prev, location: e.target.value }))}
              required
              className="w-full p-3 rounded-lg bg-muted border-0 text-sm"
              data-testid="truck-location-input"
            >
              <option value="">Select a location</option>
              {CAMPUS_LOCATIONS.map((loc) => (
                <option key={loc} value={loc}>
                  {loc}
                </option>
              ))}
            </select>
          </div>

          <div className="space-y-2" data-testid="truck-form-exact-location">
            <div>
              <Label>Exact spot (optional)</Label>
              <p className="text-xs text-muted-foreground">
                Pin exactly where this truck parks, in addition to the campus area above.
              </p>
            </div>
            <TruckLocationPicker value={exactLocation} onChange={setExactLocation} />
          </div>

          <div>
            <Label htmlFor="payout_email">Interac e-Transfer email *</Label>
            <Input
              id="payout_email"
              type="email"
              value={formData.payout_email}
              onChange={(e) => setFormData(prev => ({ ...prev, payout_email: e.target.value }))}
              placeholder="payouts@yourtruck.com"
              required
              data-testid="truck-payout-email-input"
            />
            <p className="text-xs text-muted-foreground mt-1">
              Daily earnings for this truck are sent here.
            </p>
          </div>

          <div>
            <ImagePickerField
              label="Photo"
              value={formData.photo_url}
              onChange={(url) => setFormData((prev) => ({ ...prev, photo_url: url }))}
              testId="truck-photo"
            />
          </div>

          <div>
            <Label>Operating Hours</Label>
            <p className="text-sm text-muted-foreground mb-2">
              Default: Mon-Thu 10am-6pm, Fri 10am-4pm
            </p>
            <p className="text-xs text-muted-foreground">
              You can edit hours later from the dashboard.
            </p>
          </div>

          <div className="flex gap-3 pt-4">
            <Button type="button" variant="outline" className="flex-1" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" className="flex-1" disabled={loading} data-testid="create-truck-btn">
              {loading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Create Truck
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
